import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { ApiBaseService } from "../../services/api-base.service";
import { DocumentsService } from "../../services/documents.service";
import { AppGlobals } from "../../app.globals";
import { AppConfig } from "../../app.config";
import { log } from "console";

export interface FileState {
  type: "general" | "image" | "document" | "pdf" | "video";
  name: string;
  blobUrl: string;
  mimeType: string;
}

export interface CombinedFileState {
  file: FileState | null;
  loading: LoadingState;
  error: ErrorState;
}

export interface LoadingState {
  isLoading: boolean;
  loadingText?: string;
}

export interface ErrorState {
  hasError: boolean;
  errorMessage?: string;
}

type DocEndpoint = "GetFileContent" | "GetSparkAttachmentFileContent" | "GetTaskFileContent";

@Injectable({
  providedIn: "root",
})
export class FileViewerOverlayService {
  // BehaviorSubjects for state management
  private _visible = new BehaviorSubject<boolean>(false);
  private _currentFile = new BehaviorSubject<FileState | null>(null);
  private _loadingState = new BehaviorSubject<LoadingState>({
    isLoading: false,
  });
  private _errorState = new BehaviorSubject<ErrorState>({ hasError: false });

  // Public observables
  public readonly visible$: Observable<boolean> = this._visible.asObservable();
  public readonly currentFile$: Observable<FileState | null> =
    this._currentFile.asObservable();
  public readonly loadingState$: Observable<LoadingState> =
    this._loadingState.asObservable();
  public readonly errorState$: Observable<ErrorState> =
    this._errorState.asObservable();
  public readonly fileState$: Observable<CombinedFileState> = combineLatest([
    this._currentFile.asObservable(),
    this._loadingState.asObservable(),
    this._errorState.asObservable(),
  ]).pipe(map(([file, loading, error]) => ({ file, loading, error })));

  // Internal properties
  private currentAccessToken: string = "";

  constructor(
    private apiService: ApiBaseService,
    private documentsService: DocumentsService,
    private appGlobals: AppGlobals
  ) {}

  // Public getters
  get visible(): boolean {
    return this._visible.value;
  }

  get currentFile(): FileState | null {
    return this._currentFile.value;
  }

  // Public API methods
  public async openGeneral(
    documentEndpoint: DocEndpoint,
    params: any,
    fileName: string
  ): Promise<void> {
    try {
      this.setLoadingState(true, $localize`:@@loadingFile:Loading File...`);
      this.clearError();

      await this.getAccessToken();

      const fileUrl = this.buildApiUrl(documentEndpoint, params);
      await this.loadFile(fileUrl, fileName);
    } catch (error) {
      this.setError(true, "Failed to load file");
      console.error("Error in openGeneral:", error);
    }
  }

  public async openContract(siteId: number, fileName: string): Promise<void> {
    try {
      this.setLoadingState(true, $localize`:@@loadingFile:Loading File...`);
      this.clearError();

      await this.getAccessToken();

      if (!this.currentAccessToken) {
        this.setError(true, "Failed to get access token");
        return;
      }

      const encodedName = encodeURIComponent(fileName);
      const fileUrl = `DownloadService/GetContractFileContent?id=${siteId}&name=${encodedName}&token=${this.currentAccessToken}`;

      await this.loadFile(fileUrl, fileName);
    } catch (error) {
      this.setError(true, "Failed to load contract file");
      console.error("Error in openContract:", error);
    }
  }

  public async openProjectFile(params: any, fileName: string): Promise<void> {
    try {
      this.setLoadingState(true, $localize`:@@loadingFile:Loading File...`);
      this.clearError();

      await this.getAccessToken();

      const fileUrl = this.buildApiUrl("GetFileContent", params);
      await this.loadFile(fileUrl, fileName);
    } catch (error) {
      this.setError(true, "Failed to load project file");
      console.error("Error in openProjectFile:", error);
    }
  }

  /** A file picked in the browser but not uploaded yet (e.g. "Gesellschaft erstellen"). */
  public openLocalFile(file: File): void {
    this.clearError();
    this.showBlob(file, file.name);
  }

  public close(): void {
    // Revoke blob URL if it exists
    const currentFile = this._currentFile.value;
    if (currentFile?.blobUrl) {
      URL.revokeObjectURL(currentFile.blobUrl);
    }

    // Reset state
    this._currentFile.next(null);
    this._visible.next(false);
    this.setLoadingState(false);
    this.clearError();

    // Hide global loader
    this.appGlobals.HideGlobalLoader();
  }

  // Helper methods (moved from component)
  public async getAccessToken(): Promise<void> {
    this.currentAccessToken = "";

    try {
      const result = await this.documentsService.getUserDocumentAccessToken({});

      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        this.setError(true, result.feedback);
      } else {
        this.currentAccessToken = result.accessToken;
      }
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
      this.setError(true, "Failed to get access token");
      console.error("Error getting access token:", error);
    }
  }

  public buildApiUrl(
    endpoint: string,
    params: Record<string, string | number>
  ): string {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      queryParams.append(key, value.toString());
    });

    queryParams.append("token", this.currentAccessToken);

    return `DownloadService/${endpoint}?${queryParams.toString()}`;
  }

  // Private methods
  private async loadFile(fileUrl: string, fileName: string): Promise<void> {
    try {
      const response = await this.apiService.httpGETRequest(
        fileUrl,
        {},
        true,
        "blob"
      );

      if (response instanceof Blob && response.size > 0) {
        this.showBlob(response, fileName);
      } else {
        console.error("Invalid blob response");
        this.setError(true, "Invalid file response");
        this.appGlobals.HideGlobalLoader();
      }
    } catch (error) {
      console.error("Error loading file:", error);
      this.setError(true, "Failed to load file");
      this.appGlobals.HideGlobalLoader();
      throw error;
    }
  }

  private showBlob(blob: Blob, fileName: string): void {
    const blobUrl = URL.createObjectURL(blob);
    const mimeType = blob.type;
    let fileType: "general" | "image" | "document" | "pdf" | "video" =
      "general";

    // Determine file type based on MIME type
    if (mimeType.startsWith("image/")) {
      fileType = "image";
    } else if (mimeType === "application/pdf") {
      fileType = "pdf";
    } else if (mimeType.startsWith("video/")) {
      fileType = "video";
    } else {
      fileType = "document";
    }

    // Update file state
    const fileState: FileState = {
      type: fileType,
      name: fileName,
      blobUrl: blobUrl,
      mimeType: mimeType,
    };
    console.log("File state:", fileState);

    this._currentFile.next(fileState);
    this._visible.next(true);
    this.setLoadingState(false);

    // Hide global loader with delay for PDF files
    if (fileType === "pdf") {
      setTimeout(() => {
        this.appGlobals.HideGlobalLoader();
      }, 1000);
    } else {
      this.appGlobals.HideGlobalLoader();
    }
  }

  private setLoadingState(isLoading: boolean, loadingText?: string): void {
    this._loadingState.next({
      isLoading,
      loadingText,
    });
  }

  private setError(hasError: boolean, errorMessage?: string): void {
    this._errorState.next({
      hasError,
      errorMessage,
    });
  }

  private clearError(): void {
    this._errorState.next({ hasError: false });
  }

  // Additional utility methods
  public downloadCurrentFile(): void {
    const currentFile = this._currentFile.value;
    if (currentFile?.blobUrl) {
      const link = document.createElement("a");
      link.href = currentFile.blobUrl;
      link.download = currentFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  public handleFileError(): void {
    this.setError(true, "File loading error occurred");
    this.appGlobals.HideGlobalLoader();
  }

  // Cleanup method for when service is destroyed
  public cleanup(): void {
    const currentFile = this._currentFile.value;
    if (currentFile?.blobUrl) {
      URL.revokeObjectURL(currentFile.blobUrl);
    }
  }
}
